/**
 * SignUp.tsx
 *
 * User registration page — collects account details, handles encryption key
 * generation, and triggers email verification.
 *
 * @version 1.0.0
 * @author Frontend Team
 * @date 2026-04-07
 * @modified 2026-04-07
 * @copyright © 2026 Thesis Pte Ltd. All rights reserved.
 */
import React, { useEffect, useState } from "react"
import PageLayoutAuth from "./PageLayoutAuth"
import InputDefault from "components/Input/InputDefault"
import FormInputCard from "./molecules/FormInputCard"
import Header from "./atoms/Header"
import LabelChangeRouter from "./molecules/LabelChangeRouter"
import { PATH } from "constants/path"
import {
  SignUpRequest,
  defaultSignUpRequest,
  TYPE_RESEND_EMAIL,
  CompanyRole,
} from "./types"
import { STATUS_INPUT } from "components/Input/types"
import {
  changeLabelStatusEmail,
  downLoadBackUpKey,
  isValidPhone,
  umamiTracking,
} from "helpers/utils"
import { useBoolean, useString, useWindowSize } from "helpers/hooks"
import {
  checkEmailExist,
  getCompanyRoles,
  postSendRecoveryCodeMiddleware,
  signUpMiddleware,
} from "./services/api"
import { STATUS_RESPONSE } from "types"
import { EVENT } from "constants/events"
import { pushTo } from "helpers/history"
import { MESSENGER_NOTIFICATION } from "constants/messenger"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import { toast } from "react-toastify"
import { useChangeLabelStatusInputPasswordAndConfirm } from "./auth.hook"
import FormInputSignUpCard from "./molecules/FormInputSignUpCard"
import { ACTION_RECAPTCHA } from "constants/actionReCaptcha"
import { useRecaptchaToken } from "hooks/useRecaptchaToken"
import { configureStore } from "stores/configureStore"
import { startLoading } from "reducers/loading"

import SelectMulti from "components/Select/SelectMulti"
import { cloneDeep, omit } from "lodash"
import RecoveryModal from "./molecules/RecoveryModal"
import { encryptionHelper } from "helpers/encryption"
import InputPhone from "components/Input/InputPhone"
import { DescriptionSecuityCodeCard } from "components/DescriptionSecuityCodeCard"
const SignUp = () => {
  const [request, setRequest] = useState<SignUpRequest>(defaultSignUpRequest)
  const [statusInputEmail, setStatusInputEmail] = useState({
    status: STATUS_INPUT.DEFAULT,
    label: "",
  })

  const {
    statusInputPassword,
    statusInputConfirm,
    getStatusInputPassword,
    getStatusInputConfirm,
  } = useChangeLabelStatusInputPasswordAndConfirm()

  const checkbox = useBoolean()
  const isLoading = useBoolean()
  const { isMobile } = useWindowSize()
  const showToolTip = useBoolean()
  const showToolTipSecurityCode = useBoolean()
  const { getToken, isReady } = useRecaptchaToken()
  const [cloneCompanyRoles, setCloneCompanyRoles] = useState<CompanyRole[]>([])
  const [selectedRole, setSelectedRole] = useState<CompanyRole>({
    name: "Other",
    id: "other",
  })
  const customRole = useString()
  const openRecoveryModal = useBoolean()

  useEffect(() => {
    getCompanyRoles()
      .then((res) => {
        setCloneCompanyRoles(res)
      })
      .catch((error) => { })
  }, [])

  const handleChangeInput =
    (
      key:
        | "fullname"
        | "email"
        | "company_name"
        | "password"
        | "confirmedPassword"
        | "security_code"
    ) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value
        const newRequest = cloneDeep(request)
        if (key === "company_name") {
          newRequest.company_name = value
          if (!value) {
            newRequest.company_role = {
              id: "other",
              custom_role: "",
            }
            setSelectedRole({ name: "Other", id: "other" })
          }
        } else {
          newRequest[key] = value
        }
        setRequest(newRequest)
        if (key === "email") {
          setStatusInputEmail(changeLabelStatusEmail(event.target.value))
        }
        if (key === "password") {
          getStatusInputPassword(event.target.value)
        }
        if (key === "confirmedPassword") {
          getStatusInputConfirm(event.target.value, request.password)
        }
      }

  const handleChangePhoneNumber = (value) => {
    setRequest({
      ...request,
      phoneNumber: value,
    })
  }
  const handleChangeCompanyRole = (data: { value: string; label: string }) => {
    setSelectedRole({
      name: data.label,
      id: data.value,
    })

    let newRequest = cloneDeep(request)
    if (data.value === "other") {
      newRequest.company_role = {
        id: "other",
        custom_role: "",
      }
    } else {
      newRequest.company_role = { id: data.value, custom_role: "" }
    }
    setRequest(newRequest)
  }

  const handleChangeCustomRole = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRequest = cloneDeep(request)
    newRequest.company_role = {
      id: "other",
      custom_role: event.target.value,
    }
    setRequest(newRequest)
    customRole.setValue(event.target.value)
  }

  const onClickButton = async () => {
    if (!checkbox.value) {
      toast(
        <LabelNotificationPage
          messenger={MESSENGER_NOTIFICATION.CHECKBOX_SIGN_UP}
          type="error"
        />
      )
      return
    }

    const emailExists = await checkEmailExist(
      request.email,
      request.phoneNumber
    )
    if (emailExists) return
    let backupKeyCode = request.backupKeyCode
    if (!backupKeyCode) {
      backupKeyCode = encryptionHelper.createBackupKey()
      setRequest({ ...request, backupKeyCode: backupKeyCode })
    }
    openRecoveryModal.setValue(true)
    postSendRecoveryCodeMiddleware(
      request.phoneNumber,
      request.security_code,
      backupKeyCode,
      request.email,
      request.fullname
    )
  }
  const onSubmit = async () => {
    isLoading.setValue(true)
    configureStore.dispatch(startLoading())
    try {
      const token = await getToken(ACTION_RECAPTCHA.SIGN_UP)
      const dataRequest: SignUpRequest = {
        ...omit(request, "confirmedPassword"),
        captcha: token,
      }
      umamiTracking(EVENT.SIGNUP, {
        email: request.email,
      })
      signUpMiddleware(dataRequest, (type: STATUS_RESPONSE, messenger) => {
        isLoading.setValue(false)
        if (type === STATUS_RESPONSE.SUCCESS) {
          pushTo(PATH.confirmEmail, "", {
            type: TYPE_RESEND_EMAIL.VERIFICATION,
            email: request.email,
          })
        } else {
          toast(
            <LabelNotificationPage messenger={messenger ?? ""} type="error" />
          )
        }
      })
    } catch {
      isLoading.setValue(false)
    }
  }

  const isValidPhoneNumber = isValidPhone(request.phoneNumber)

  const handleDisableButtonAccount = () => {
    const {
      fullname,
      email,
      password,
      confirmedPassword,
      company_name,
      company_role,
      security_code,
    } = request
    const isEmailError = statusInputEmail.status === STATUS_INPUT.ERROR
    const isPasswordError = statusInputPassword.status === STATUS_INPUT.ERROR

    if (!fullname) return true
    if (!email || isEmailError) return true
    if (!password || isPasswordError) return true
    if (confirmedPassword !== password) return true
    if (!isValidPhoneNumber) return true
    if (security_code.length !== 4) return true
    if (company_name) {
      if (!company_role?.id) return true
      if (company_role.id === "other" && !company_role.custom_role) return true
    }

    return false
  }

  const onKeyPressInput = (
    event: React.KeyboardEvent<HTMLDivElement>
  ): void => {
    if (event.key === "Enter") {
      event.preventDefault()
      if (handleDisableButtonAccount()) {
        return
      }
      onClickButton()
    }
  }

  const onResendEmail = () => {
    postSendRecoveryCodeMiddleware(
      request.phoneNumber,
      request.security_code,
      request.backupKeyCode,
      request.email,
      request.fullname
    )
  }
  return (
    <PageLayoutAuth>
      <div className="h-full w-full flex flex-col px-6 py-4">
        <Header title="Sign up to Tracelium" />
        <LabelChangeRouter
          title="Already have an account?"
          label="Sign in"
          path={PATH.login}
          styleRoot={{
            marginTop: 12,
          }}
        />
        <div className="mt-3">
          <FormInputCard title="Full Name" required>
            <InputDefault
              value={request.fullname}
              onChange={handleChangeInput("fullname")}
              onKeyPress={onKeyPressInput}
            />
          </FormInputCard>
          <FormInputCard title="Email" required>
            <InputDefault
              value={request.email}
              onChange={handleChangeInput("email")}
              status={statusInputEmail.status}
              labelStatus={statusInputEmail.label}
              onKeyPress={onKeyPressInput}
            />
          </FormInputCard>
          <FormInputCard title="Phone number" required>
            <InputPhone
              value={request.phoneNumber}
              setValue={handleChangePhoneNumber}
              onEnterKeyPress={onKeyPressInput}
            />
            {request.phoneNumber ? (
              <div className="flex mt-1">
                <p
                  className="font-semibold"
                  style={{
                    color: isValidPhoneNumber ? "#56A012" : "#EA4545",
                    fontSize: 10,
                    lineHeight: "14px",
                  }}
                >
                  {isValidPhoneNumber
                    ? "Valid phone number format!"
                    : "Invalid phone number format!"}
                </p>
              </div>
            ) : null}
          </FormInputCard>
          <FormInputCard
            title="Set up a passcode"
            required
            isTippy
            label={<DescriptionSecuityCodeCard />}
            showToolTip={showToolTipSecurityCode}
            visible={isMobile ? showToolTipSecurityCode.value : undefined}
            onClickOutside={() =>
              isMobile ? showToolTipSecurityCode.setValue(false) : undefined
            }
          >
            <InputDefault
              value={request.security_code}
              type="password"
              isPassword
              onChange={handleChangeInput("security_code")}
              onKeyPress={onKeyPressInput}
              placeholder="4 characters"
              maxLength={4}
            />
          </FormInputCard>
          <FormInputCard
            title="Password"
            required
            isTippy
            label={MESSENGER_NOTIFICATION.PASSWORD_VALIDATE}
            showToolTip={showToolTip}
            visible={isMobile ? showToolTip.value : undefined}
            onClickOutside={() =>
              isMobile ? showToolTip.setValue(false) : undefined
            }
          >
            <InputDefault
              type="password"
              isPassword
              value={request.password}
              onChange={handleChangeInput("password")}
              onKeyPress={onKeyPressInput}
              status={statusInputPassword.status}
              labelStatus={statusInputPassword.label}
            />
          </FormInputCard>
          <FormInputCard
            title="Confirm password"
            required
            isTippy
            showToolTip={showToolTip}
            visible={isMobile ? showToolTip.value : undefined}
            onClickOutside={() =>
              isMobile ? showToolTip.setValue(false) : undefined
            }
          >
            <InputDefault
              type="password"
              isPassword
              value={request.confirmedPassword}
              onChange={handleChangeInput("confirmedPassword")}
              onKeyPress={onKeyPressInput}
              status={statusInputConfirm.status}
              labelStatus={statusInputConfirm.label}
            />
          </FormInputCard>
          <FormInputCard title="Company " label="(Optional)">
            <InputDefault
              value={request.company_name}
              onChange={handleChangeInput("company_name")}
              onKeyPress={onKeyPressInput}
            />
          </FormInputCard>
          {request.company_name && (
            <>
              <FormInputCard title="Role" required>
                <SelectMulti
                  options={[
                    ...cloneCompanyRoles.map((role) => {
                      return { label: role.name, value: role.id }
                    }),
                    { label: "Other", value: "other" },
                  ]}
                  selectedOption={{
                    label: selectedRole?.name,
                    value: selectedRole?.id,
                  }}
                  handleChange={handleChangeCompanyRole}
                  isMulti={false}
                  noOptionsMessage
                ></SelectMulti>
              </FormInputCard>
              {selectedRole?.id === "other" && (
                <FormInputCard title="Role name" required>
                  <InputDefault
                    value={customRole.value}
                    onChange={handleChangeCustomRole}
                    onKeyPress={onKeyPressInput}
                  />
                </FormInputCard>
              )}
            </>
          )}
        </div>
        <FormInputSignUpCard
          checkbox={checkbox}
          onClickButton={onClickButton}
          handleDisableButton={handleDisableButtonAccount() || !isReady}
          styleRoot={{
            marginBottom: 12,
          }}
        />
      </div>
      {openRecoveryModal.value && request.backupKeyCode && (
        <RecoveryModal
          handleDisplayCloseIcon={() => () => openRecoveryModal.setValue(false)}
          backUpKey={request.backupKeyCode}
          cancelText="Download"
          onCloseModal={() => {
            downLoadBackUpKey(
              request.backupKeyCode,
              request.email,
              request.phoneNumber,
              request.security_code
            )
          }}
          onResendEmail={onResendEmail}
          submitText="Continue"
          onClickSubmit={onSubmit}
        />
      )}
    </PageLayoutAuth>
  )
}
export default SignUp
